<template>
  <div class="dialog">
    <span class="cls" @click="close"></span>
    <div class="paper">
      <!-- Header -->
      <div class="header">
        <span class="back" @click="close">
          <i class="ph ph-caret-left"></i>
        </span>
        <h2>청구사례</h2>
        <span class="more" @click="toggleMenu" v-if="isMyPost">
          <i class="ph ph-dots-three"></i>
        </span>
        <span class="more" v-else></span>

        <!-- Dropdown Menu -->
        <div class="dropdown-menu" v-if="showMenu">
          <button @click="editPost">
            <i class="ph ph-pencil-simple"></i>
            수정
          </button>
          <button @click="deletePost" class="danger">
            <i class="ph ph-trash"></i>
            삭제
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="content">
        <!-- Post Section -->
        <div class="post-section">
          <!-- Category & Date -->
          <div class="post-top">
            <span class="category-badge">{{ getCategoryLabel(post.category) }}</span>
            <span class="post-date">{{ formatDate(post.created_at) }}</span>
          </div>

          <h2 class="post-title">{{ post.title }}</h2>

          <div class="post-author">
            <div class="author-avatar">
              {{ getInitial(post.mb_id) }}
            </div>
            <span class="author-name">{{ post.mb_id }}</span>
          </div>

          <div class="post-body" v-html="linkify(post.content)"></div>

          <!-- Images -->
          <div class="post-images" v-if="images.length > 0">
            <div 
              class="image-thumb" 
              v-for="(img, idx) in images" 
              :key="idx"
              @click="openImageViewer(idx)"
            >
              <img :src="img" alt="첨부이미지" />
            </div>
          </div>

          <!-- Actions -->
          <div class="post-actions">
            <button 
              class="action-btn like-btn" 
              :class="{ active: isLiked }" 
              @click="toggleLike"
              :disabled="likeLoading"
            >
              <i :class="isLiked ? 'ph-fill ph-heart' : 'ph ph-heart'"></i>
              <span>{{ post.like_count || 0 }}</span>
            </button>
            <div class="action-btn">
              <i class="ph ph-chat-circle"></i>
              <span>{{ post.comment_count || 0 }}</span>
            </div>
            <div class="action-btn">
              <i class="ph ph-eye"></i>
              <span>{{ post.view_count || 0 }}</span>
            </div>
          </div>
        </div>

        <!-- Comment Section -->
        <div class="comment-section">
          <div class="section-title">댓글 {{ comments.length }}</div>

          <!-- Comment List -->
          <div class="comment-list" v-if="comments.length > 0">
            <div 
              class="comment-item" 
              v-for="comment in comments" 
              :key="comment.idx"
            >
              <div class="comment-avatar">
                {{ getInitial(comment.mb_id) }}
              </div>
              <div class="comment-body">
                <div class="comment-top">
                  <span class="comment-author">{{ comment.mb_id }}</span>
                  <span class="comment-date">{{ formatDate(comment.created_at) }}</span>
                </div>
                <p class="comment-content">{{ comment.content }}</p>
                <img v-if="comment.sticker" :src="comment.sticker" class="comment-sticker" />
              </div>
              <!-- 내 댓글이면 삭제 버튼 -->
              <button 
                v-if="comment.mb_id === mb_id" 
                class="comment-delete"
                @click="deleteComment(comment.idx)"
              >
                <i class="ph ph-x"></i>
              </button>
            </div>
          </div>

          <div class="empty-comment" v-else>
            <i class="ph ph-chat-circle-dots"></i>
            <p>첫 번째 댓글을 남겨보세요</p>
          </div>
        </div>
      </div>

      <!-- Comment Input (Fixed Bottom) -->
      <div class="comment-input-wrap">
        <div v-if="selectedSticker" class="claim-sticker-preview">
          <img :src="selectedSticker" />
          <span class="sticker-remove" @click="selectedSticker = ''">&times;</span>
        </div>
        <div class="comment-input">
          <input 
            type="text" 
            v-model="newComment" 
            placeholder="댓글을 입력하세요"
            @keyup.enter="submitComment"
          />
          <button class="sticker-toggle-btn" @click="showSticker = true">
            <i class="ph ph-sticker"></i>
          </button>
          <button 
            class="send-btn" 
            @click="submitComment" 
            :disabled="(!newComment.trim() && !selectedSticker) || commentLoading"
          >
            <i class="ph ph-paper-plane-tilt"></i>
          </button>
        </div>
      </div>
      <StickerSelector v-model="showSticker" @select="onStickerSelect" />

      <!-- Image Viewer -->
      <div class="image-viewer" v-if="showImageViewer" @click="showImageViewer = false">
        <div class="viewer-header">
          <span class="viewer-counter">{{ currentImageIdx + 1 }} / {{ images.length }}</span>
          <button class="viewer-close" @click="showImageViewer = false">
            <i class="ph ph-x"></i>
          </button>
        </div>
        <div class="viewer-body" @click.stop>
          <button class="viewer-nav prev" @click="prevImage" v-if="images.length > 1">
            <i class="ph ph-caret-left"></i>
          </button>
          <img :src="images[currentImageIdx]" alt="이미지" />
          <button class="viewer-nav next" @click="nextImage" v-if="images.length > 1">
            <i class="ph ph-caret-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Dialog - View 위에 표시되도록 dialog 내부 최상단에 배치 -->
    <ClaimExEdit 
      v-if="showEditDialog" 
      :oth_showClaimExEdit="handleEditClose"
      :editIdx="editPostIdx"
    />
  </div>
</template>

<script>
import { ref, inject, computed, onMounted, onUnmounted } from "vue"
import ClaimExEdit from "@/components/dialogs/claimExEdit.vue"
import StickerSelector from "@/components/dialogs/StickerSelector.vue"

export default {
  components: { ClaimExEdit, StickerSelector },
  props: {
    oth_showClaimExView: Function,
    postIdx: { required: true }
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const member_info = inject('member_info')
    const mb_id = member_info.mb_id

    const post = ref({})
    const images = ref([])
    const comments = ref([])
    const newComment = ref('')
    const showMenu = ref(false)
    const showImageViewer = ref(false)
    const currentImageIdx = ref(0)
    const isLiked = ref(false)
    const likeLoading = ref(false)
    const commentLoading = ref(false)
    const showSticker = ref(false)
    const selectedSticker = ref('')

    function onStickerSelect(url) {
      selectedSticker.value = url
    }

    // Edit 다이얼로그 (View 내부에서 관리 - z-index 문제 해결)
    const showEditDialog = ref(false)
    const editPostIdx = ref(null)

    const categories = [
      { label: '전체', value: 'all' },
      { label: '암', value: 'cancer' },
      { label: '자동차', value: 'car' },
      { label: '3대질병', value: 'major3' },
      { label: '실손', value: 'actual' },
      { label: '기타', value: 'etc' }
    ]

    const isMyPost = computed(() => post.value.mb_id === mb_id)

    const getCategoryLabel = (value) => {
      const cat = categories.find(c => c.value === value)
      return cat ? cat.label : '기타'
    }

    const getInitial = (id) => {
      if (!id) return '?'
      return id.charAt(0).toUpperCase()
    }

    // SQL 문자열 이스케이프
    const escapeSQL = (str) => {
      if (!str) return ''
      return str.replace(/\\/g, '\\\\').replace(/'/g, "''")
    }

    // 내 좋아요 상태 확인 (기존 likes 테이블 활용)
    const checkMyLike = async () => {
      try {
        const rows = await sql_query(`
          SELECT idx FROM likes 
          WHERE target_table = 'claim_ex_post' 
            AND target_idx = ${props.postIdx} 
            AND mb_id = '${escapeSQL(mb_id)}'
        `)
        isLiked.value = rows && rows.length > 0
      } catch (e) {
        console.log('Check like error:', e)
        isLiked.value = false
      }
    }

    // 게시글 조회
    const loadPost = async () => {
      try {
        await sql_query(`
          UPDATE claim_ex_post 
          SET view_count = view_count + 1 
          WHERE idx = ${props.postIdx}
        `)
      } catch (e) {
        console.log('View count update error:', e)
      }

      const rows = await sql_query(`
        SELECT idx, mb_id, title, content, category, images, 
               like_count, comment_count, view_count, created_at
        FROM claim_ex_post
        WHERE idx = ${props.postIdx}
      `)
      
      if (rows && rows.length > 0) {
        post.value = rows[0]
        try {
          images.value = rows[0].images ? JSON.parse(rows[0].images) : []
        } catch (e) {
          images.value = []
        }
      }
    }

    // 댓글 목록 조회 (기존 comment 테이블 활용)
    const loadComments = async () => {
      try {
        const rows = await sql_query(`
          SELECT idx, mb_id, content, sticker, created_at
          FROM comment
          WHERE target_table = 'claim_ex_post' 
            AND target_idx = ${props.postIdx}
            AND status = 'normal'
          ORDER BY created_at ASC
        `)
        comments.value = rows || []
      } catch (e) {
        console.log('Load comments error:', e)
        comments.value = []
      }
    }

    // 댓글 등록 (기존 comment 테이블 활용)
    const submitComment = async () => {
      const content = newComment.value.trim()
      if (!content || commentLoading.value) return

      commentLoading.value = true

      try {
        const stickerVal = selectedSticker.value || ''
        await sql_query(`
          INSERT INTO comment (mb_id, target_table, target_idx, content, sticker)
          VALUES ('${escapeSQL(mb_id)}', 'claim_ex_post', ${props.postIdx}, '${escapeSQL(content)}', '${stickerVal}')
        `)

        await sql_query(`
          UPDATE claim_ex_post 
          SET comment_count = comment_count + 1 
          WHERE idx = ${props.postIdx}
        `)

        newComment.value = ''
        selectedSticker.value = ''
        await loadPost()
        await loadComments()
      } catch (e) {
        console.error('댓글 등록 실패:', e)
        alert('댓글 등록에 실패했습니다.')
      } finally {
        commentLoading.value = false
      }
    }

    // 댓글 삭제 (status를 deleted로 변경)
    const deleteComment = async (commentIdx) => {
      if (!confirm('댓글을 삭제하시겠습니까?')) return

      try {
        await sql_query(`
          UPDATE comment SET status = 'deleted'
          WHERE idx = ${commentIdx} AND mb_id = '${escapeSQL(mb_id)}'
        `)

        await sql_query(`
          UPDATE claim_ex_post 
          SET comment_count = GREATEST(comment_count - 1, 0)
          WHERE idx = ${props.postIdx}
        `)

        await loadPost()
        await loadComments()
      } catch (e) {
        console.error('댓글 삭제 실패:', e)
      }
    }

    // 좋아요 토글 (기존 likes 테이블 활용)
    const toggleLike = async () => {
      if (likeLoading.value) return
      likeLoading.value = true

      try {
        if (isLiked.value) {
          // 좋아요 취소
          await sql_query(`
            DELETE FROM likes 
            WHERE target_table = 'claim_ex_post' 
              AND target_idx = ${props.postIdx} 
              AND mb_id = '${escapeSQL(mb_id)}'
          `)
          await sql_query(`
            UPDATE claim_ex_post 
            SET like_count = GREATEST(like_count - 1, 0)
            WHERE idx = ${props.postIdx}
          `)
          isLiked.value = false
        } else {
          // 좋아요 추가
          await sql_query(`
            INSERT INTO likes (mb_id, target_table, target_idx)
            VALUES ('${escapeSQL(mb_id)}', 'claim_ex_post', ${props.postIdx})
          `)
          await sql_query(`
            UPDATE claim_ex_post 
            SET like_count = like_count + 1 
            WHERE idx = ${props.postIdx}
          `)
          isLiked.value = true
        }
        await loadPost()
      } catch (e) {
        console.error('좋아요 처리 실패:', e)
      } finally {
        likeLoading.value = false
      }
    }

    const toggleMenu = () => {
      showMenu.value = !showMenu.value
    }

    // Edit 다이얼로그 열기 (View 내부에서 관리)
    const editPost = () => {
      showMenu.value = false
      editPostIdx.value = props.postIdx
      showEditDialog.value = true
    }

    // Edit 다이얼로그 닫기 핸들러
    const handleEditClose = (show, options = {}) => {
      if (!show) {
        showEditDialog.value = false
        editPostIdx.value = null
        if (options.refresh) {
          loadPost()
        }
      }
    }

    const deletePost = async () => {
      if (!confirm('정말 삭제하시겠습니까?')) return
      
      showMenu.value = false
      
      // 댓글 삭제 (status를 deleted로 변경)
      try {
        await sql_query(`UPDATE comment SET status = 'deleted' WHERE target_table = 'claim_ex_post' AND target_idx = ${props.postIdx}`)
      } catch (e) { 
        console.log('댓글 삭제 중 오류:', e) 
      }

      // 좋아요 삭제
      try {
        await sql_query(`DELETE FROM likes WHERE target_table = 'claim_ex_post' AND target_idx = ${props.postIdx}`)
      } catch (e) { 
        console.log('좋아요 삭제 중 오류:', e) 
      }
      
      await sql_query(`
        DELETE FROM claim_ex_post WHERE idx = ${props.postIdx} AND mb_id = '${escapeSQL(mb_id)}'
      `)
      
      props.oth_showClaimExView(false, { refresh: true })
    }

    const close = () => {
      props.oth_showClaimExView(false)
    }

    // Image Viewer
    const openImageViewer = (idx) => {
      currentImageIdx.value = idx
      showImageViewer.value = true
    }

    const prevImage = () => {
      if (currentImageIdx.value > 0) {
        currentImageIdx.value--
      } else {
        currentImageIdx.value = images.value.length - 1
      }
    }

    const nextImage = () => {
      if (currentImageIdx.value < images.value.length - 1) {
        currentImageIdx.value++
      } else {
        currentImageIdx.value = 0
      }
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) return '방금 전'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}일 전`
      
      return `${date.getMonth() + 1}월 ${date.getDate()}일`
    }

    // URL을 클릭 가능한 링크로 변환
    const linkify = (text) => {
      if (!text) return ''
      const urlPattern = /(https?:\/\/[^\s<]+[^\s<.,;:!?"'\])>])/g
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      return escaped.replace(urlPattern, '<a href="$1" target="_blank" rel="noopener">$1</a>')
    }

    // 외부 클릭 시 메뉴 닫기
    const handleClickOutside = (e) => {
      if (!e.target.closest('.more') && !e.target.closest('.dropdown-menu')) {
        showMenu.value = false
      }
    }

    onMounted(async () => {
      await loadPost()
      await checkMyLike()
      await loadComments()
      document.addEventListener('click', handleClickOutside)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      post,
      images,
      comments,
      newComment,
      showMenu,
      showImageViewer,
      currentImageIdx,
      isLiked,
      likeLoading,
      commentLoading,
      isMyPost,
      mb_id,
      showEditDialog,
      editPostIdx,
      getCategoryLabel,
      getInitial,
      linkify,
      showSticker,
      selectedSticker,
      onStickerSelect,
      submitComment,
      deleteComment,
      toggleLike,
      toggleMenu,
      editPost,
      handleEditClose,
      deletePost,
      close,
      openImageViewer,
      prevImage,
      nextImage,
      formatDate
    }
  }
}
</script>

<style scoped>
.dialog {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: var(--z-index-top);
  backdrop-filter: blur(var(--effect-blur));
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  position: relative;
  width: 90%;
  max-width: 600px;
  height: 85%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--color-background-light);
  position: relative;
}

.header > h2 {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0;
}

.header > .back,
.header > .more {
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: var(--radius-sm);
}

.header > .back:active,
.header > .more:active {
  background: var(--color-background-mute);
}

.header > .back > i,
.header > .more > i {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
}

.dropdown-menu {
  position: absolute;
  top: 52px;
  right: 16px;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 10;
  min-width: 120px;
}

.dropdown-menu button {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  cursor: pointer;
}

.dropdown-menu button > i {
  font-size: var(--font-size-base);
}

.dropdown-menu button:active {
  background: var(--color-background-mute);
}

.dropdown-menu button.danger {
  color: var(--color-special-warning);
}

/* Content */
.content {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 70px;
}

/* Post Section */
.post-section {
  padding: 16px;
  border-bottom: 8px solid var(--color-background-mute);
}

.post-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.category-badge {
  display: inline-block;
  padding: 4px 10px;
  background: var(--color-brand-light);
  border-radius: 4px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-brand);
}

.post-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
}

.post-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0 0 16px 0;
  line-height: 1.4;
}

.post-author {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.author-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--color-brand);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-white);
}

.author-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.post-body {
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  line-height: 1.8;
  white-space: pre-wrap;
}

.post-body :deep(a) {
  color: var(--color-brand);
  text-decoration: underline;
  word-break: break-all;
}

/* Images */
.post-images {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  overflow-x: auto;
}

.post-images::-webkit-scrollbar {
  display: none;
}

.image-thumb {
  width: 80px;
  height: 80px;
  border-radius: var(--radius-md);
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
}

.image-thumb > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Actions */
.post-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--color-background-light);
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: var(--font-size-sm);
  color: var(--color-text-third);
}

.action-btn > i {
  font-size: var(--font-size-lg);
}

.like-btn {
  padding: 6px 12px;
  border: 1px solid var(--color-background-light);
  border-radius: 20px;
  background: var(--color-background);
  cursor: pointer;
  transition: all 0.2s;
}

.like-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.like-btn.active {
  border-color: #ff6b6b;
  background: #fff0f0;
  color: #ff6b6b;
}

.like-btn.active > i {
  color: #ff6b6b;
}

/* Comment Section */
.comment-section {
  padding: 16px;
}

.section-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin-bottom: 16px;
}

.comment-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.comment-item {
  display: flex;
  gap: 10px;
  position: relative;
}

.comment-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-special-point);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-white);
  flex-shrink: 0;
}

.comment-body {
  flex: 1;
  min-width: 0;
}

.comment-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.comment-author {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.comment-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
}

.comment-content {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  line-height: 1.5;
  margin: 0;
  word-break: break-word;
}

.comment-delete {
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-third);
  opacity: 0.6;
  flex-shrink: 0;
}

.comment-delete:hover {
  opacity: 1;
  color: var(--color-special-warning);
}

.empty-comment {
  padding: 32px 0;
  text-align: center;
}

.empty-comment > i {
  font-size: 40px;
  color: var(--color-text-third);
  margin-bottom: 8px;
}

.empty-comment > p {
  color: var(--color-text-third);
  margin: 0;
  font-size: var(--font-size-sm);
}

/* Comment Input */
.comment-input-wrap {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  padding: 12px 16px;
  background: var(--color-background);
  border-top: 1px solid var(--color-background-light);
  box-sizing: border-box;
}

.comment-input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 4px 4px 16px;
  background: var(--color-background-mute);
  border: 1px solid var(--color-background-light);
  border-radius: 24px;
}

.comment-input > input {
  flex: 1;
  border: none;
  background: none;
  padding: 8px 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  outline: none;
}

.comment-input > input::placeholder {
  color: var(--color-text-third);
}

.send-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: var(--color-brand);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.send-btn > i {
  font-size: var(--font-size-lg);
  color: var(--color-text-white);
}

/* Image Viewer */
.image-viewer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  flex-direction: column;
  z-index: 100;
}

.viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  flex-shrink: 0;
}

.viewer-counter {
  font-size: var(--font-size-sm);
  color: rgba(255, 255, 255, 0.8);
}

.viewer-close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-close > i {
  font-size: 24px;
  color: #fff;
}

.viewer-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0 16px;
}

.viewer-body > img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: var(--radius-md);
}

.viewer-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-nav.prev {
  left: 8px;
}

.viewer-nav.next {
  right: 8px;
}

.viewer-nav > i {
  font-size: 24px;
  color: #fff;
}

.sticker-toggle-btn {
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sticker-toggle-btn > i {
  font-size: 22px;
  color: var(--color-text-secondary);
}

.claim-sticker-preview {
  position: relative;
  display: inline-block;
  padding: 4px 0 8px;
}

.claim-sticker-preview img {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
  background: var(--color-background-light, #f5f5f5);
}

.sticker-remove {
  position: absolute;
  top: 0;
  right: -6px;
  width: 20px;
  height: 20px;
  background: #e74c3c;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  line-height: 1;
}

.comment-sticker {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
  margin-top: 4px;
}
</style>